'use client';

import { useEffect } from 'react';
import { Logger } from '@/core/utils/logger';
const appLogger = new Logger('app');
import { initConsoleFilter, setConsoleFilter } from '@/shared/utils/consoleFilter';
import '../core/utils/debug-api-errors'; // Import debug helpers
import { apiLogger, componentsLogger } from '../core/utils/logger';
import { cookieUtils } from '@/core/auth/utils/cookieUtils';
import { config } from '@/config/config';
import { baseApi } from '@/api/core/baseApi';
import { useAppDispatch } from '@/core/store/hooks';
import { usePrefetchCoordinator } from '@/core/hooks/usePrefetchCoordinator';
import { initUserIdentity } from '@/core/auth/services/userIdentity';
import { initPerfObservers, getPerfSnapshot } from '@/shared/utils/perf';
import { setupUnhandledRejectionTracker, getRecentRejections } from '@/core/utils/unhandledRejectionTracker';
import { recoverSessionState } from '@/core/auth/services/sessionRecovery';
import { attachWindowAttentionListeners } from '@/core/utils/windowAttention';

// This component initializes client-side only features
export function ClientInit() {
	const dispatch = useAppDispatch();
	
	// Single coordinated background prefetch (low priority, no duplicates)
	usePrefetchCoordinator({ 
		enabled: true, 
		priority: 'low' 
	});
	useEffect(() => {
		// Initialize lightweight perf observers (CLS, long tasks). FPS optional to keep overhead tiny.
		try {
			initPerfObservers({ observeCLS: true, observeLongTasks: true, observeFPS: false });
		} catch {}

		// Expose a helper to quickly inspect perf state in dev tools
		try {
			if (typeof window !== 'undefined') {
				window.getVoleyPerf = () => getPerfSnapshot();
			}
		} catch {}
		// Cleanup legacy cookies on boot to reduce surface area
		try {
			cookieUtils.clearAuthCookies();
		} catch {}
		// Initialize user identity system
		initUserIdentity();
		
		// Initialize console filtering
		initConsoleFilter();
		
		// Setup unhandled rejection tracking for dev/staging environments
		// This provides detailed logging and Sentry breadcrumbs for debugging
		setupUnhandledRejectionTracker();

		// Setup global error handlers for production only
		if (config.env === 'production') {
			window.onerror = (message, source, lineno, colno, error) => {
				appLogger.error('Global error:', error || message);
				return true;
			};
		}
		
		// Add global debug helpers
		if (typeof window !== 'undefined') {
				(window as Window & { enableVerboseLogs?: () => void }).enableVerboseLogs = () => {
					setConsoleFilter(false);
					apiLogger.info('Verbose logging enabled');
				};

				(window as Window & { disableVerboseLogs?: () => void }).disableVerboseLogs = () => {
					setConsoleFilter(true);
					apiLogger.info('Verbose logging disabled');
				};
			
			// Add global function to show feedback dialog
			(window as Window & { reportBug?: () => void }).reportBug = () => {
				// Check if Sentry is available (only on internal pages)
				if ((window as Window & { Sentry?: { showReportDialog?: () => void } }).Sentry?.showReportDialog) {
					(window as Window & { Sentry?: { showReportDialog?: () => void } }).Sentry?.showReportDialog?.();
				} else {
					componentsLogger.info('Bug reporting is only available on internal pages');
				}
			};
			
			// Debug helper for viewing recent unhandled rejections
			(window as Window & { getRecentRejections?: () => unknown[] }).getRecentRejections = getRecentRejections;

			componentsLogger.info('App initialized. Use enableVerboseLogs() or disableVerboseLogs() to control console output.');
			componentsLogger.info('🐛 Use reportBug() to open the feedback dialog manually.');
			apiLogger.info('Use debugApiErrors() to view API errors, debugAuthInfo() for auth details, getRecentRejections() for promise errors.');
		}
		
		// Reset RTK Query cache on build change
		try {
			const buildKey = 'app_build';
			const currentBuild = process.env.NEXT_PUBLIC_APP_BUILD as string | undefined;
			const previousBuild = typeof window !== 'undefined' ? localStorage.getItem(buildKey) : null;
			if (currentBuild && previousBuild !== currentBuild) {
				apiLogger.info(`[ClientInit] Detected new build: ${currentBuild} (prev: ${previousBuild ?? 'none'}). Resetting API cache.`);
				dispatch(baseApi.util.resetApiState());
				localStorage.setItem(buildKey, String(currentBuild));
			}
		} catch (e) {
			appLogger.warn('[ClientInit] Failed to reset cache on build change:', e);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Recover gracefully after idle: on focus/visible/online, validate and refresh CSRF
	useEffect(() => {
		if (typeof window === 'undefined') return;

		const recover = async () => {
			await recoverSessionState({ requireInvalidated: false });
		};

		return attachWindowAttentionListeners(recover, {
			onVisible: true,
			onFocus: true,
			onOnline: true,
			minIntervalMs: 250,
		});
	}, []);

	return null;
}